import React from 'react';
import { tooltipStyle } from './chartTheme';

const boxStyle = {
  ...tooltipStyle,
  padding: '8px 12px',
  background: '#fff',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: 12,
  lineHeight: 1.35,
  color: '#334155',
};

const nameStyle = { fontWeight: 600, color: '#1e293b' };
const valueStyle = { fontVariantNumeric: 'tabular-nums', color: '#475569' };
const mutedStyle = { color: '#94a3b8' };
const headerStyle = {
  marginBottom: 6,
  fontSize: 12,
  fontWeight: 600,
  color: '#1e293b',
};

function dot(color) {
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
      aria-hidden
    />
  );
}

/**
 * Bar/line tooltips — "Series 123", not Recharts default "Series : 123".
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  valueFormatter,
}) {
  if (!active || !payload?.length) return null;

  const rows = payload.filter((p) => p.value != null && p.value !== '');
  if (!rows.length) return null;

  const header = label != null && String(label).trim() !== '' ? String(label) : null;

  return (
    <div style={boxStyle}>
      {header ? <p style={headerStyle}>{header}</p> : null}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((entry) => {
          const raw = entry.value;
          const shown = valueFormatter ? valueFormatter(raw) : raw;
          const key = `${entry.name}-${entry.dataKey ?? ''}-${entry.color ?? ''}`;
          return (
            <li key={key} style={{ ...rowStyle, marginTop: header ? 4 : 0 }}>
              {entry.color ? dot(entry.color) : null}
              <span style={nameStyle}>{entry.name}</span>
              <span style={valueStyle}>{shown}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Donut slice tooltip — matches legend: "Wins 1 (50%)" (no colon).
 */
export function CompositionDonutTooltip({
  active,
  payload,
  total,
  valueFormatter,
}) {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  if (!entry || entry.value == null) return null;

  const raw = entry.value;
  const name = entry.name;
  const shown = valueFormatter ? valueFormatter(raw) : raw;
  const pct = total ? Math.round((Number(raw) / total) * 100) : 0;
  const color = entry.payload?.color || entry.color;

  return (
    <div style={boxStyle}>
      <p style={rowStyle}>
        {color ? dot(color) : null}
        <span style={nameStyle}>{name}</span>
        <span style={valueStyle}>
          {shown}
          <span style={mutedStyle}> ({pct}%)</span>
        </span>
      </p>
    </div>
  );
}

/** Shared Recharts Tooltip props — avoids default "label : value" separator. */
export const chartTooltipProps = {
  separator: ' ',
  cursor: { fill: 'rgba(148, 163, 184, 0.12)' },
  contentStyle: tooltipStyle,
  itemStyle: { padding: 0 },
  labelStyle: { marginBottom: 4, fontWeight: 600, color: '#1e293b' },
};

/**
 * Default Recharts tooltip formatter helper (bar/line).
 * Use with separator=" " so labels read "Series 123" not "Series : 123".
 */
export function chartTooltipFormatter(valueFormatter) {
  return (value, name) => {
    const shown = valueFormatter ? valueFormatter(value) : value;
    return [shown, name];
  };
}
